import { Controller, useForm } from 'react-hook-form'

import { useDropdown } from '@/entities/dropdowns/libs/useDropdown'
import { DropdownOption } from '@/entities/dropdowns/models/DropdownOption'
import { AddressFields } from '@/shared/components/AddressFields/AddressFields'
import { SelectDropdown } from '@/shared/components/SelectDropdown/SelectDropdown'
import { TextField } from '@/shared/components/TextField/TextField'
import { pxToRem } from '@/shared/styles/themes'
import { Alert, Box, Divider, Grid2 as Grid, styled, Typography } from '@mui/material'

import { DepotFormValues, GroupFormValues } from '../models/OnboardingFormFields'

export type DepotSetupFormProps = {
  initialValues: DepotFormValues
  groupData: GroupFormValues
  onSubmit: (data: DepotFormValues) => void
  isSubmitting: boolean
}

export const DepotSetupForm: React.FC<DepotSetupFormProps> = ({
  initialValues,
  groupData,
  onSubmit,
  isSubmitting,
}) => {
  const { control, handleSubmit } = useForm<DepotFormValues>({
    defaultValues: {
      ...initialValues,
      // We store just the reference to the group ID, not the whole object
      groupId: groupData.id,
    },
  })

  const { data: colorOptions, completed: fetchColorsCompleted } = useDropdown('htmlColors')

  const onFormSubmit = (data: DepotFormValues) => {
    // Ensure we're only sending the group ID reference
    data.groupId = groupData.id
    onSubmit(data)
  }

  return (
    <StyledForm id="onboarding-step-2" onSubmit={handleSubmit(onFormSubmit)}>
      <Typography variant="h2" sx={{ fontSize: pxToRem(24), fontWeight: 500, mb: 2 }}>
        Set Up Your First Depot
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        Depots are your distribution centers or warehouses. This information will help set up operations and routing.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Depots define the starting and ending points for your delivery runs.
      </Alert>

      {/* General Information Section */}
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
        General Information
      </Typography>
      <Grid container columnSpacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="externalId"
            control={control}
            rules={{ required: 'Depot code is required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Depot Code"
                placeholder="Enter depot code"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                required
                disabled={isSubmitting}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Depot name is required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Depot Name"
                placeholder="Enter depot name"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                required
                disabled={isSubmitting}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="uiColor"
            control={control}
            rules={{ required: 'Color is required' }}
            render={({ field, fieldState }) => (
              <SelectDropdown
                {...field}
                label="Depot UI Color"
                disableClearable={true}
                placeholder="Select color"
                options={colorOptions}
                onChange={(_, value) => field.onChange(value)}
                value={field.value}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                getOptionLabel={(option) => option?.label || ''}
                disabled={!fetchColorsCompleted || isSubmitting}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                required
                renderOption={(props, option) => {
                  const { ...optionProps } = props

                  return (
                    <Box
                      component="li"
                      {...optionProps}
                    >
                      <Box
                        sx={{
                          width: pxToRem(12),
                          height: pxToRem(12),
                          borderRadius: pxToRem(2),
                          marginRight: pxToRem(8),
                          backgroundColor: (option as DropdownOption).value,
                        }}
                      />
                      {(option as DropdownOption).label}
                    </Box>
                  )
                }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="groupId"
            control={control}
            render={() => (
              <TextField
                label="Group"
                value={groupData.name}
                disabled={true}
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Address Section */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
        Depot Address
      </Typography>
      <Grid container columnSpacing={2}>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="address"
            control={control}
            rules={{
              required: true,
              validate: (value) => {
                if (!value.street || !value.suburb || !value.state || !value.postcode || !value.country) {
                  return 'All address fields are required'
                }

                return true
              },
            }}
            render={({ field, fieldState }) => (
              <AddressFields
                {...field}
                value={field.value}
                onChange={field.onChange}
                error={!!fieldState.error}
                disabled={isSubmitting}
              />
            )}
          />
        </Grid>
      </Grid>
    </StyledForm>
  )
}

const StyledForm = styled('form')`
  width: 100%;
`